<script>
  import { onMount } from 'svelte';
  import emailjs from '@emailjs/browser';

  const LS_KEY = 'htp_about_bio';
  const EMAILJS_SERVICE  = 'service_jw6vokg';
  const EMAILJS_TEMPLATE = 'template_cs43t5g';
  const EMAILJS_KEY      = 'xDB6OdpLQ_VclPeOG';

  let bio = '';
  let sent = false;
  let sending = false;
  let sendError = '';
  let form = { name: '', email: '', message: '' };

  onMount(async () => {
    const saved = localStorage.getItem(LS_KEY);
    if (saved) {
      bio = saved;
    } else {
      try {
        const res = await fetch('/about.json');
        if (res.ok) ({ bio } = await res.json());
      } catch (_) {}
    }
  });

  async function submit(e) {
    e.preventDefault();
    sending = true;
    sendError = '';
    try {
      await emailjs.send(EMAILJS_SERVICE, EMAILJS_TEMPLATE, {
        name:    form.name,
        email:   form.email,
        message: form.message,
        time:    new Date().toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }),
      }, EMAILJS_KEY);
      sent = true;
    } catch (err) {
      sendError = 'Something went wrong — please try again.';
    } finally {
      sending = false;
    }
  }
</script>

<svelte:head>
  <title>About — Hanna Tiina Pekk</title>
  <meta name="description" content={bio ? bio.slice(0, 155) + (bio.length > 155 ? '…' : '') : 'About Hanna Tiina Pekk, fashion designer.'} />
  <meta property="og:title"       content="About — Hanna Tiina Pekk" />
  <meta property="og:description" content={bio ? bio.slice(0, 155) + (bio.length > 155 ? '…' : '') : 'About Hanna Tiina Pekk, fashion designer.'} />
  <meta name="twitter:title"       content="About — Hanna Tiina Pekk" />
  <meta name="twitter:description" content={bio ? bio.slice(0, 155) + (bio.length > 155 ? '…' : '') : 'About Hanna Tiina Pekk, fashion designer.'} />
</svelte:head>

<div class="about">
  <div class="about-inner">
    <section class="bio-section">
      <h2 class="section-label">About</h2>
      <p class="bio">{bio}</p>
      <div class="social">
        <a href="https://www.instagram.com/hannatiinapekk/" target="_blank" rel="noopener noreferrer" class="social-link" aria-label="Instagram">
          <svg class="ig-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
            <circle cx="12" cy="12" r="4"/>
            <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none"/>
          </svg>
        </a>
        <a href="mailto:hannapekk@gmail.com" class="email-link">hannapekk@gmail.com</a>
      </div>
    </section>

    <section class="contact-section">
      <h2 class="section-label">Contact</h2>

      {#if sent}
        <p class="thanks">Thank you — I'll be in touch soon.</p>
      {:else}
        <form class="contact-form" on:submit={submit}>
          <label>
            <span>Name</span>
            <input type="text" bind:value={form.name} required placeholder="Your name" />
          </label>
          <label>
            <span>Email</span>
            <input type="email" bind:value={form.email} required placeholder="your@email.com" />
          </label>
          <label>
            <span>Message</span>
            <textarea bind:value={form.message} required rows="5" placeholder="Write your message here…"></textarea>
          </label>
          <button type="submit" disabled={sending}>{sending ? 'Sending…' : 'Send'}</button>
          {#if sendError}<p class="send-error">{sendError}</p>{/if}
        </form>
      {/if}
    </section>
  </div>
</div>

<style>
  .about {
    max-width: 680px;
    margin: 0 auto;
    padding: 3rem 1.5rem 6rem;
  }

  .about-inner {
    display: flex;
    flex-direction: column;
    gap: 4rem;
  }

  .section-label {
    font-size: 0.7rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #aaa;
    font-weight: 400;
    margin: 0 0 1.2rem;
  }

  .contact-section .section-label {
    font-size: 1rem;
    color: #1a1a1a;
    font-weight: 700;
  }

  .bio {
    font-size: 0.95rem;
    line-height: 1.9;
    color: #333;
    margin: 0;
  }

  /* Social / email */
  .social {
    display: flex;
    align-items: center;
    gap: 1.2rem;
    margin-top: 1.5rem;
  }

  .social-link {
    display: flex;
    color: #1a1a1a;
    transition: color 0.2s;
  }

  .social-link:hover {
    color: #888;
  }

  .ig-icon {
    width: 22px;
    height: 22px;
  }

  .email-link {
    font-size: 0.82rem;
    letter-spacing: 0.04em;
    color: #1a1a1a;
    text-decoration: none;
    border-bottom: 1px solid #ccc;
    padding-bottom: 1px;
    transition: color 0.2s, border-color 0.2s;
  }

  .email-link:hover {
    color: #888;
    border-color: #888;
  }

  /* Contact form */
  .contact-form {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
  }

  label {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  label span {
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #1a1a1a;
  }

  input, textarea {
    font-family: inherit;
    font-size: 0.85rem;
    color: #1a1a1a;
    background: transparent;
    border: none;
    border-bottom: 1px solid #ddd;
    padding: 0.4rem 0;
    outline: none;
    transition: border-color 0.2s;
    resize: none;
  }

  input:focus, textarea:focus {
    border-bottom-color: #1a1a1a;
  }

  input::placeholder, textarea::placeholder {
    color: #ccc;
  }

  button {
    align-self: flex-start;
    margin-top: 0.5rem;
    background: #1a1a1a;
    color: #f8f6f3;
    border: none;
    font-family: inherit;
    font-size: 0.75rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    padding: 0.75rem 2rem;
    cursor: pointer;
    transition: background 0.2s;
  }

  button:hover {
    background: #333;
  }

  .send-error {
    font-size: 0.78rem;
    color: #b00;
    margin: 0.5rem 0 0;
  }

  .thanks {
    font-size: 0.85rem;
    color: #555;
    margin: 0;
  }
</style>
